// Frontend API module for DecisionOS Worker endpoints

// Get API base URL from environment or fallback to default
export const getApiBaseUrl = () => {
  // REACT_APP_API_URL should be set at build time
  // Dev: http://localhost or dev worker
  // Prod: https://api.getdecisionos.com (custom domain for prod worker)
  return process.env.REACT_APP_API_URL || 'https://api.getdecisionos.com';
};

const API_BASE_URL = getApiBaseUrl();

console.log('[API_JS] API_BASE_URL:', API_BASE_URL);
console.log('[API_JS] Environment:', process.env.NODE_ENV);

// Dev-only fallback Slack ID - only used when slackId not provided in DEV_MODE
// In production, real Slack ID comes from authenticated session
// This allows local development without full OAuth setup
const MOCK_SLACK_ID = null; // No default - must be provided by caller

// Helper function to make API calls with proper headers
async function apiCall(endpoint, options = {}, slackId = MOCK_SLACK_ID) {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  // Only add X-Slack-Id header in dev mode, otherwise rely on session cookie
  // Check both NODE_ENV and explicit REACT_APP_DEV_MODE flag
  const isDevMode = process.env.NODE_ENV === 'development' && 
    (process.env.REACT_APP_DEV_MODE === 'false' || process.env.REACT_APP_DEV_MODE !== 'false');
  
  if (isDevMode && slackId) {
    headers['X-Slack-Id'] = slackId;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include' // Include cookies for session auth
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}: ${response.statusText}` };
      }
      console.error(`API call failed for ${endpoint}:`, response.status, errorData);
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    // Enhanced error logging
    if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
      console.error(`API call failed for ${endpoint}: Network error or CORS issue.`);
      console.error('URL:', url);
      console.error('Check: 1) Backend deployed? 2) CORS headers? 3) Credentials set?');
    }
    console.error(`API call error for ${endpoint}:`, error);
    throw error;
  }
}

// API functions

export async function getQuarters(slackId = MOCK_SLACK_ID) {
  return apiCall('/api/quarters', {}, slackId);
}

export async function getOKRs(scope = 'me', quarter, slackId = MOCK_SLACK_ID) {
  const params = new URLSearchParams({ scope, quarter });
  return apiCall(`/api/okrs?${params}`, {}, slackId);
}

export async function getMyPlan(quarter, slackId = MOCK_SLACK_ID) {
  const params = new URLSearchParams({ quarter });
  return apiCall(`/api/myplan?${params}`, {}, slackId);
}

export async function getProposals(status = 'Pending', scope = 'me', slackId = MOCK_SLACK_ID) {
  const params = new URLSearchParams({ status, scope });
  return apiCall(`/api/proposals?${params}`, {}, slackId);
}

export async function createProposal(proposal, slackId = MOCK_SLACK_ID) {
  return apiCall('/api/proposals', {
    method: 'POST',
    body: JSON.stringify(proposal)
  }, slackId);
}

export async function updateProposal(proposalId, action, slackId = MOCK_SLACK_ID) {
  return apiCall(`/api/proposals/${proposalId}`, {
    method: 'PATCH',
    body: JSON.stringify({ action })
  }, slackId);
}

export async function getHotspots(scope = 'team', quarter, limit = 3, slackId = MOCK_SLACK_ID) {
  const params = new URLSearchParams({ scope, quarter, limit: limit.toString() });
  return apiCall(`/api/hotspots?${params}`, {}, slackId);
}

export async function getDrift(scope = 'me', quarter, slackId = MOCK_SLACK_ID) {
  const params = new URLSearchParams({ scope, quarter });
  return apiCall(`/api/drift?${params}`, {}, slackId);
}

export async function getMetrics(scope = 'me', quarter, slackId = MOCK_SLACK_ID) {
  const params = new URLSearchParams({ scope, quarter });
  return apiCall(`/api/metrics?${params}`, {}, slackId);
}

export async function getHomeSuggestion(quarter, slackId = MOCK_SLACK_ID) {
  const params = new URLSearchParams({ quarter });
  return apiCall(`/api/home/suggestion?${params}`, {}, slackId);
}

// Create a new capture (task)
export async function createCapture(text, slackId = MOCK_SLACK_ID) {
  return apiCall('/api/captures', {
    method: 'POST',
    body: JSON.stringify({ text })
  }, slackId);
}

// Update a capture (task) - status, text, or due_date
export async function updateCapture(captureId, updates, slackId = MOCK_SLACK_ID) {
  return apiCall(`/api/captures/${captureId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates)
  }, slackId);
}

// Link a capture (task) to a KR by Airtable recordId
export async function linkCapture(captureId, krRecordId, slackId = MOCK_SLACK_ID) {
  return apiCall('/api/captures/link', {
    method: 'POST',
    body: JSON.stringify({ captureId, krRecordId })
  }, slackId);
}

// Delete a capture (task) permanently
export async function deleteCapture(captureId, slackId = MOCK_SLACK_ID) {
  return apiCall(`/api/captures/${captureId}`, {
    method: 'DELETE'
  }, slackId);
}

// Parse OKRs from raw text using GPT
export async function parseOKRs(text, slackId = MOCK_SLACK_ID) {
  try {
    const response = await apiCall('/api/okr/parse', {
      method: 'POST',
      body: JSON.stringify({ text })
    }, slackId);

    return response.objectives || [];
  } catch (error) {
    console.error('Error parsing OKRs:', error);
    throw error;
  }
}

// Helper function to save OKRs (creates OKRs directly in Airtable)
export async function saveOKRs(okrData, quarter, slackId = MOCK_SLACK_ID) {
  try {
    // Map polarity from frontend format to Airtable format
    const mapPolarity = (polarity) => {
      if (polarity === 'down') return 'Lower is better';
      if (polarity === 'up') return 'Higher is better';
      return polarity || 'Higher is better';
    };
    
    // Prepare the payload for the new OKR creation endpoint
    const payload = {
      okrs: [{
        objective: okrData.objective || '',
        quarter: quarter || '',
        keyResults: (okrData.keyResults || []).map(kr => ({
          title: kr.title || '',
          type: kr.type ? (kr.type.charAt(0).toUpperCase() + kr.type.slice(1)) : 'Milestone',
          target: kr.target || null,
          unit: kr.unit || null,
          polarity: mapPolarity(kr.polarity)
        }))
      }]
    };
    
    // Only include ownerSlackId if provided (dev mode)
    // In production, backend will get Slack ID from session cookie
    if (slackId) {
      payload.ownerSlackId = slackId;
    }

    const response = await apiCall('/api/okr/wizard/save', {
      method: 'POST',
      body: JSON.stringify(payload)
    }, slackId);

    if (response.ok) {
      return { 
        success: true, 
        objCount: response.objCount,
        krCount: response.krCount,
        message: `OKRs created successfully! ${response.objCount} objectives, ${response.krCount} key results.`
      };
    } else {
      throw new Error(response.error || 'Failed to create OKRs');
    }
  } catch (error) {
    console.error('Error saving OKRs:', error);
    throw error;
  }
}

// Get tour completion status
export async function getTourCompleted(slackId = MOCK_SLACK_ID) {
  try {
    const response = await apiCall('/api/user/tour-completed', {}, slackId);
    return response.tourCompleted === true;
  } catch (error) {
    console.error('Error getting tour completion:', error);
    // Default to false if error (show tour)
    return false;
  }
}

// Update tour completion status
export async function updateTourCompleted(tourCompleted, slackId = MOCK_SLACK_ID) {
  try {
    const response = await apiCall('/api/user/tour-completed', {
      method: 'PATCH',
      body: JSON.stringify({ tourCompleted })
    }, slackId);
    return response.tourCompleted === true;
  } catch (error) {
    console.error('Error updating tour completion:', error);
    throw error;
  }
}

// Update an objective (name and/or quarter)
export async function updateObjective(objectiveId, updates, slackId = MOCK_SLACK_ID) {
  try {
    const response = await apiCall(`/api/objectives/${objectiveId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates) // { name?, quarter? }
    }, slackId);
    return response;
  } catch (error) {
    console.error('Error updating objective:', error);
    throw error;
  }
}

// Update a KR (full edit: text, type, target, unit, etc.)
export async function updateKR(krId, updates, slackId = MOCK_SLACK_ID) {
  try {
    const response = await apiCall(`/api/kr/${krId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates) // { text?, type?, target?, unit?, etc. }
    }, slackId);
    return response;
  } catch (error) {
    console.error('Error updating KR:', error);
    throw error;
  }
}

// Create a new KR under an existing objective
export async function createKR(payload, slackId = MOCK_SLACK_ID) {
  try {
    const response = await apiCall('/api/kr', {
      method: 'POST',
      body: JSON.stringify(payload)
    }, slackId);
    return response;
  } catch (error) {
    console.error('Error creating KR:', error);
    throw error;
  }
}

// Delete a KR
export async function deleteKR(krId, slackId = MOCK_SLACK_ID) {
  try {
    const response = await apiCall(`/api/kr/${krId}`, {
      method: 'DELETE'
    }, slackId);
    return response;
  } catch (error) {
    console.error('Error deleting KR:', error);
    throw error;
  }
}

// Delete an Objective (and its KRs)
export async function deleteObjective(objectiveId, slackId = MOCK_SLACK_ID) {
  try {
    const response = await apiCall(`/api/objectives/${objectiveId}`, {
      method: 'DELETE'
    }, slackId);
    return response;
  } catch (error) {
    console.error('Error deleting objective:', error);
    throw error;
  }
}

// Get user profile with data counts
export async function getUserProfile() {
  try {
    const response = await apiCall('/api/user/profile', {
      method: 'GET'
    });
    return response;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
}

// Delete user account
export async function deleteUserAccount() {
  try {
    const response = await apiCall('/api/user/account', {
      method: 'DELETE',
      body: JSON.stringify({ confirmation: 'DELETE' })
    });
    return response;
  } catch (error) {
    console.error('Error deleting user account:', error);
    throw error;
  }
}

// Helper to invalidate home/drift cache after mutations
export function invalidateHomeCache() {
  try {
    // Clear all home_drift_* keys from sessionStorage
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith('home_drift_')) {
        sessionStorage.removeItem(key);
      }
    });
    console.log('[HOME_CACHE] Cache invalidated');
  } catch (e) {
    console.warn('[HOME_CACHE] Failed to invalidate cache:', e);
  }
}
